import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import '../styles/FAQPage.css';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      category: 'About Cordyceps',
      questions: [
        {
          q: 'What is Cordyceps militaris?',
          a: 'Cordyceps militaris is an adaptogenic mushroom known for supporting natural energy, endurance, and stress adaptation. It works by supporting cellular energy production rather than stimulating the nervous system, making it suitable for regular wellness use.'
        },
        {
          q: 'What is the difference between Cordyceps militaris and Cordyceps sinensis?',
          a: 'Cordyceps sinensis is a rare wild fungus traditionally found in high-altitude regions and is often expensive and difficult to verify. Cordyceps militaris is a cultivated species that can be grown in controlled environments and lab-tested for safety and potency. Cordyceps militaris contains high levels of cordycepin and is widely used today due to its consistency, sustainability, and reliability.'
        },
        {
          q: 'Is Cordyceps militaris vegan?',
          a: 'Yes, cultivated Cordyceps militaris is completely vegan. It is grown on plant-based substrates such as brown rice, without harming insects or animals, making it suitable for vegan and vegetarian lifestyles.'
        },
        {
          q: 'What is cordycepin and why is it important?',
          a: 'Cordycepin is a key bioactive compound found in Cordyceps militaris. It is associated with many of the mushroom\'s functional benefits and is often used as a marker of quality and potency.'
        },
        {
          q: 'Is Cordyceps scientifically proven?',
          a: 'Cordyceps has both a long history of traditional use and growing scientific research. Modern studies focus on its role in energy metabolism, oxygen utilization, immune balance, and stress response.'
        }
      ]
    },
    {
      category: 'Benefits & Effects',
      questions: [
        {
          q: 'What does Cordyceps do for energy?',
          a: 'Cordyceps supports energy by improving how the body produces and uses ATP, the cell\'s main energy source. This results in steady, sustained energy rather than a quick spike or crash.'
        },
        {
          q: 'How is Cordyceps different from coffee?',
          a: 'Coffee provides quick energy by stimulating the nervous system through caffeine. Cordyceps works differently. It supports the body\'s natural energy production at the cellular level, helping improve endurance and reduce fatigue without jitters or energy crashes. Many people use Cordyceps for sustained energy rather than instant stimulation.'
        },
        {
          q: 'How is Cordyceps different from caffeine?',
          a: 'Caffeine stimulates the nervous system and can cause jitters or crashes. Cordyceps supports energy at the cellular level, leading to more balanced and sustained energy without overstimulation.'
        },
        {
          q: 'Does Cordyceps help with fatigue?',
          a: 'Yes, Cordyceps is widely studied for its role in reducing fatigue. It supports oxygen efficiency and cellular energy production, which may help improve stamina and recovery.'
        },
        {
          q: 'Is Cordyceps good for immunity?',
          a: 'Cordyceps supports immune balance rather than aggressively boosting immunity. Research suggests it helps regulate immune responses and supports antioxidant activity in the body.'
        },
        {
          q: 'Can athletes benefit from Cordyceps?',
          a: 'Yes. Research suggests Cordyceps may support oxygen uptake, endurance, and recovery. It\'s popular among athletes, fitness enthusiasts, and anyone seeking to enhance physical performance naturally.'
        },
        {
          q: 'Is Cordyceps safe for athletes?',
          a: 'Yes, Cordyceps is commonly used by athletes and active individuals because it supports endurance and recovery without stimulants. High-quality, tested Cordyceps does not contain banned substances.'
        }
      ]
    },
    {
      category: 'Usage & Dosage',
      questions: [
        {
          q: 'How much Cordyceps should I take daily?',
          a: 'A typical dosage ranges from 1-3 grams (1000-3000 mg) of dried fruiting body or powder per day. Start with a lower dose (1 gram) and gradually increase based on your body\'s response. Always follow product-specific guidelines.'
        },
        {
          q: 'When is the best time to take Cordyceps?',
          a: 'Cordyceps is usually taken in the morning or early afternoon. Since it is not a stimulant, it typically does not interfere with sleep, though individual responses may vary.'
        },
        {
          q: 'How long does it take to feel the effects?',
          a: 'Effects vary by individual. Some people notice increased energy and focus within the first few days, while others may take 2-4 weeks of consistent use to experience the full adaptogenic benefits. Cordyceps works best as a long-term wellness support, not a quick fix.'
        },
        {
          q: 'How long does Cordyceps take to work?',
          a: 'Some people notice benefits within a few days, while others experience gradual improvements over a few weeks. Cordyceps works best when taken consistently over time.'
        },
        {
          q: 'Can I take Cordyceps every day?',
          a: 'Yes. Cordyceps militaris has an excellent safety profile and is suitable for daily, long-term use. Consistency is key to experiencing its full adaptogenic benefits.'
        },
        {
          q: 'Should I cycle Cordyceps or take breaks?',
          a: 'Cycling is not necessary for most people. Cordyceps works as an adaptogen, meaning it helps normalize bodily functions rather than overstimulate them. However, some prefer to take periodic breaks (e.g., 5 days on, 2 days off) based on personal preference.'
        },
        {
          q: 'Can Cordyceps be taken long term?',
          a: 'Yes, Cordyceps is commonly used long term when taken in recommended amounts. Cultivated Cordyceps militaris has shown a good safety profile in studies and traditional use.'
        }
      ]
    },
    {
      category: 'Safety & Interactions',
      questions: [
        {
          q: 'Is Cordyceps safe?',
          a: 'Yes. Scientific studies have shown Cordyceps militaris has an excellent safety profile with no observed toxic effects at commonly used doses. Lab-grown Cordyceps avoids contamination risks associated with wild harvesting.'
        },
        {
          q: 'Is Cordyceps safe for daily use?',
          a: 'Yes, Cordyceps militaris is generally considered safe for daily use when it is properly cultivated and lab-tested. Scientific studies have not shown toxic effects at commonly used doses.'
        },
        {
          q: 'Are there any side effects?',
          a: 'Cordyceps is generally well-tolerated. Mild digestive discomfort may occur in rare cases, usually when starting with a high dose. If you experience any adverse reactions, discontinue use and consult a healthcare provider.'
        },
        {
          q: 'Does Cordyceps have any side effects?',
          a: 'Cordyceps is well tolerated by most people. Some individuals may experience mild digestive discomfort initially, which usually settles as the body adjusts. Serious side effects are rare with high-quality, cultivated Cordyceps.'
        },
        {
          q: 'Can I take Cordyceps with other supplements?',
          a: 'Yes, Cordyceps is often combined with other adaptogens like Ashwagandha, Reishi, or Rhodiola. However, if you\'re taking medications or have specific health conditions, consult a healthcare professional first.'
        },
        {
          q: 'Can Cordyceps be taken with other supplements?',
          a: 'Cordyceps is generally compatible with most wellness supplements. However, people on medication should consult a healthcare professional before combining supplements.'
        },
        {
          q: 'Is Cordyceps safe during pregnancy or breastfeeding?',
          a: 'There is limited research on Cordyceps use during pregnancy or breastfeeding. As a precaution, consult your healthcare provider before use if you are pregnant, nursing, or planning to become pregnant.'
        },
        {
          q: 'Who should avoid taking Cordyceps?',
          a: 'Pregnant or breastfeeding individuals, and those with autoimmune conditions, should consult a healthcare professional before using Cordyceps regularly.'
        },
        {
          q: 'Can children take Cordyceps?',
          a: 'Cordyceps is generally considered safe, but dosing for children has not been extensively studied. Consult a pediatrician before giving Cordyceps to children.'
        }
      ]
    },
    {
      category: 'Quality & Sourcing',
      questions: [
        {
          q: 'Why is lab-grown Cordyceps better than wild-harvested?',
          a: 'Lab-grown Cordyceps militaris ensures consistent quality, purity, and potency. It eliminates contamination risks (heavy metals, pesticides), environmental destruction, and adulteration common with wild harvesting. It\'s also more sustainable and affordable.'
        },
        {
          q: 'What is the difference between wild and cultivated Cordyceps?',
          a: 'Wild Cordyceps is rare, expensive, and often contaminated. Cultivated Cordyceps militaris is grown in controlled environments and can be lab-tested for purity, safety, and potency.'
        },
        {
          q: 'Is cultivated Cordyceps better than wild Cordyceps?',
          a: 'Yes, cultivated Cordyceps militaris offers consistent quality, higher safety, and environmental sustainability. It avoids the contamination and adulteration risks associated with wild harvesting.'
        },
        {
          q: 'How do I know if Cordyceps is high quality?',
          a: 'Look for products that are 100% Cordyceps militaris fruiting body (not mycelium on grain), organic, lab-tested for cordycepin content, free from fillers and additives, and backed by transparent testing documentation.'
        },
        {
          q: 'How do I know if a Cordyceps product is pure?',
          a: 'Pure Cordyceps should be cultivated, lab-tested for contaminants, and transparent about its growing process. Products without testing or origin details should be approached cautiously.'
        },
        {
          q: 'Why does quality control matter so much with Cordyceps?',
          a: 'Cordyceps absorbs compounds from its growing environment. Controlled cultivation and lab testing ensure safety, consistent potency, and freedom from heavy metals or microbial contamination.'
        },
        {
          q: 'Should I choose fruiting body or mycelium products?',
          a: 'Fruiting body is generally preferred. It contains the full spectrum of bioactive compounds, including cordycepin. Mycelium-on-grain products often contain fillers (grain starch) and lower active compound levels.'
        }
      ]
    },
    {
      category: 'Storage & Shelf Life',
      questions: [
        {
          q: 'How should I store Cordyceps?',
          a: 'Store Cordyceps in a cool, dry place away from direct sunlight, heat, and moisture. Keep the container tightly sealed to preserve potency. Refrigeration is not necessary but can extend shelf life.'
        },
        {
          q: 'How long does Cordyceps last?',
          a: 'When stored properly, dried Cordyceps and powder typically last 1-2 years. Tinctures can last 2-5 years. Always check the expiration date on the product label.'
        }
      ]
    },
    {
      category: 'Use in Pets',
      questions: [
        {
          q: 'Can Cordyceps be given to pets?',
          a: 'Yes. Cordyceps has been used in veterinary herbal practice, especially for aging pets, low stamina, joint discomfort, and recovery support. It is considered safe when used in appropriate doses.'
        },
        {
          q: 'What are the main benefits of Cordyceps for pets?',
          a: 'Cordyceps may help pets by supporting energy and stamina (without overstimulation), reducing joint inflammation and stiffness, improving exercise tolerance in older dogs, supporting immune balance, and aiding recovery after illness or surgery. It is particularly useful for aging, low-activity, or frequently fatigued pets.'
        },
        {
          q: 'Is Cordyceps safe for long-term use in pets?',
          a: 'Yes. Cordyceps is non-stimulatory and non-addictive, making it suitable for long-term or maintenance use under veterinary guidance.'
        },
        {
          q: 'Will Cordyceps make my pet hyperactive?',
          a: 'No. Cordyceps does not act like a stimulant. Most pet owners report improved willingness to move, better endurance, and reduced stiffness—not hyperactivity.'
        },
        {
          q: 'Can Cordyceps be given with other pet supplements or medicines?',
          a: 'Generally yes, but it is recommended to inform your veterinarian if your pet is on long-term medication and avoid combining with strong immune-stimulating drugs unless advised.'
        },
        {
          q: 'Which pets benefit the most from Cordyceps?',
          a: 'Cordyceps is especially helpful for senior dogs and cats, pets with joint stiffness, pets with low stamina or slow recovery, and working or active dogs during recovery phases.'
        }
      ]
    },
    {
      category: 'Use in Aging & Elderly People',
      questions: [
        {
          q: 'Is Cordyceps suitable for elderly people?',
          a: 'Yes. Cordyceps is widely used to support healthy aging, particularly for fatigue, reduced physical stamina, joint discomfort, slow recovery, and immune imbalance. It is gentle and well tolerated.'
        },
        {
          q: 'How does Cordyceps help aging bodies?',
          a: 'Cordyceps supports aging individuals by improving cellular energy efficiency, reducing chronic low-grade inflammation, supporting joint and muscle recovery, enhancing oxygen utilization, and supporting immune balance. It helps people do daily activities with less fatigue, not by stimulation but by efficiency.'
        },
        {
          q: 'Will Cordyceps interfere with sleep in elderly people?',
          a: 'No. Cordyceps does not disturb sleep when taken earlier in the day. In many cases, improved recovery and reduced inflammation actually support better sleep quality.'
        },
        {
          q: 'How long does it take to see results in elderly users?',
          a: 'Reduced fatigue: 2–3 weeks, Joint comfort: 3–5 weeks, Sustained stamina & recovery: 4–8 weeks. Benefits build gradually and are more noticeable over time.'
        },
        {
          q: 'Is Cordyceps safe for long-term daily use in seniors?',
          a: 'Yes. Cordyceps is considered safe for long-term use, especially when used at moderate doses, combined with proper sleep and nutrition, and taken as part of a wellness routine.'
        },
        {
          q: 'Who should be cautious before using Cordyceps?',
          a: 'Consult a healthcare professional if the person is on immunosuppressive therapy, has undergone organ transplantation, or has severe autoimmune conditions. For most elderly individuals, Cordyceps is well tolerated.'
        }
      ]
    },
    {
      category: 'Important Clarification',
      questions: [
        {
          q: 'Is Cordyceps an energy booster or a stimulant?',
          a: 'Cordyceps is not a stimulant. It supports energy efficiency, recovery, and fatigue resistance. It does not cause dependency, crashes, or forced exertion when used responsibly.'
        }
      ]
    },
    {
      category: 'Mushhrushh Specific',
      questions: [
        {
          q: 'Where is Mushhrushh Cordyceps grown?',
          a: 'Our Cordyceps militaris is cultivated in our state-of-the-art laboratory facilities in Goa, India, under strictly controlled, sterile conditions.'
        },
        {
          q: 'Is Mushhrushh Cordyceps tested?',
          a: 'Yes. Every batch undergoes rigorous third-party testing for cordycepin content, potency, purity, and safety. We provide full traceability from culture to your doorstep.'
        },
        {
          q: 'Do you use any fillers or additives?',
          a: 'No. Our products contain 100% pure Cordyceps militaris with no fillers, flow agents, or synthetic additives.'
        },
        {
          q: 'How can I purchase Mushhrushh products?',
          a: 'Please contact us directly for product inquiries, bulk orders, or to learn more about our Cordyceps offerings.'
        }
      ]
    }
  ];

  const toggleFAQ = (categoryIndex, questionIndex) => {
    const index = `${categoryIndex}-${questionIndex}`;
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="faq-page">
      <div className="page-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <h1 className="hero-medium">
            Frequently Asked <span className="accent-orange">Questions</span>
          </h1>
          <p className="body-large page-intro">
            Everything you need to know about Cordyceps militaris, from benefits to usage, safety, and quality.
          </p>
        </div>
      </div>

      <div className="page-content section-padding">
        <div className="container">
          <div className="faq-wrapper">
            {faqs.map((category, catIndex) => (
              <div key={catIndex} className="faq-category">
                <h2 className="heading-2 category-title">{category.category}</h2>
                <div className="questions-list">
                  {category.questions.map((faq, qIndex) => {
                    const index = `${catIndex}-${qIndex}`;
                    const isOpen = openIndex === index;
                    
                    return (
                      <div key={qIndex} className={`faq-item ${isOpen ? 'open' : ''}`}>
                        <button
                          className="faq-question"
                          onClick={() => toggleFAQ(catIndex, qIndex)}
                        >
                          <span className="question-text">{faq.q}</span>
                          <ChevronDown 
                            size={20} 
                            className={`chevron-icon ${isOpen ? 'rotated' : ''}`}
                          />
                        </button>
                        <div className={`faq-answer ${isOpen ? 'expanded' : ''}`}>
                          <p className="body-regular">{faq.a}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="contact-cta">
            <h2 className="heading-2">Still Have Questions?</h2>
            <p className="body-large">
              We're here to help. Reach out to us for personalized guidance on Cordyceps and our products.
            </p>
            <Link to="/#contact" className="btn-primary btn-orange">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPage;